import { useMap } from "@repo/ui/components/map";
import * as maplibregl from "maplibre-gl";
import { useEffect, useRef } from "react";
const SOURCE_ID = "places";
const LAYER_NORMAL = "places-normal";
const LAYER_DIMMED = "places-dimmed";
const LAYER_SELECTED = "places-selected";

// Category slugs that have generated icons in /public/map-icons/
const CATEGORY_SLUGS = ["mat-dryck", "natur", "kultur", "sport-fritid", "boende", "halsa", "service", "offentligt"];

// Icon name → parent category slug for image lookup
const ICON_TO_SLUG = {
  utensils: "mat-dryck",
  coffee: "mat-dryck",
  beer: "mat-dryck",
  sandwich: "mat-dryck",
  trees: "natur",
  "tree-pine": "natur",
  mountain: "natur",
  waves: "natur",
  footprints: "natur",
  landmark: "kultur",
  "building-2": "kultur",
  clapperboard: "kultur",
  "book-open": "kultur",
  church: "kultur",
  dumbbell: "sport-fritid",
  trophy: "sport-fritid",
  baby: "sport-fritid",
  "circle-dot": "sport-fritid",
  bed: "boende",
  hotel: "boende",
  tent: "boende",
  home: "boende",
  "heart-pulse": "halsa",
  hospital: "halsa",
  pill: "halsa",
  smile: "halsa",
  store: "service",
  "shopping-bag": "service",
  banknote: "service",
  scissors: "service",
  fuel: "service",
  building: "offentligt",
  "graduation-cap": "offentligt",
  shield: "offentligt",
  "door-open": "offentligt"
};
const COLOR_STATES = ["default", "pending", "verified"];
function loadImg(map, id, src) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      if (!map.hasImage(id)) map.addImage(id, img, {
        sdf: false
      });
      resolve();
    };
    img.onerror = () => resolve();
    img.src = src;
  });
}
async function loadAllIcons(map) {
  const tasks = [];
  const base = "/map-icons";
  const allSlugs = ["default", ...CATEGORY_SLUGS];
  for (const slug of allSlugs) {
    for (const color of COLOR_STATES) {
      const prefix = `${slug}-${color}`;
      tasks.push(loadImg(map, `pin-${prefix}`, `${base}/${prefix}.svg`));
      tasks.push(loadImg(map, `pin-${prefix}-sel`, `${base}/${prefix}-sel.svg`));
      tasks.push(loadImg(map, `pin-${prefix}-dim`, `${base}/${prefix}-dim.svg`));
    }
  }
  await Promise.all(tasks);
}
function categorySlug(iconName) {
  if (!iconName) return "default";
  return ICON_TO_SLUG[iconName] ?? "default";
}
function applyData(map, places) {
  const src = map.getSource(SOURCE_ID);
  if (!src) return;
  src.setData({
    type: "FeatureCollection",
    features: places.map(p => {
      const cat = categorySlug(p.categoryIcon);
      const color = p.hasVerifiedFeature ? "verified" : p.hasPendingFeature ? "pending" : "default";
      return {
        type: "Feature",
        properties: {
          id: p.id,
          name: p.name,
          pinImg: `pin-${cat}-${color}`,
          pinImgSel: `pin-${cat}-${color}-sel`,
          pinImgDim: `pin-${cat}-${color}-dim`
        },
        geometry: {
          type: "Point",
          coordinates: p.location.coordinates
        }
      };
    })
  });
}
function applySelection(map, id) {
  const has = id != null;
  map.setFilter(LAYER_NORMAL, has ? ["==", "id", "__none__"] : null);
  map.setFilter(LAYER_DIMMED, has ? ["!=", ["get", "id"], id] : ["==", "id", "__none__"]);
  map.setFilter(LAYER_SELECTED, has ? ["==", ["get", "id"], id] : ["==", "id", "__none__"]);
}
export function PlacePinsLayer({
  places,
  selectedPlaceId,
  onSelectPlace,
  onClearSelection
}) {
  const {
    map,
    isLoaded
  } = useMap();
  const layersReadyRef = useRef(false);
  const prevPlacesCount = useRef(0);

  // Latest-callback refs to avoid re-binding click handlers on every render
  const cbSelect = useRef(onSelectPlace);
  cbSelect.current = onSelectPlace;
  const cbClear = useRef(onClearSelection);
  cbClear.current = onClearSelection;
  const latestPlaces = useRef(places);
  latestPlaces.current = places;
  const latestSelected = useRef(selectedPlaceId);
  latestSelected.current = selectedPlaceId;

  // Initialize source + layers + handlers (once per map instance)
  useEffect(() => {
    if (!isLoaded || !map) return;
    let cancelled = false;
    const setup = async () => {
      await loadAllIcons(map);
      if (cancelled) return;
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
      map.addLayer({
        id: LAYER_NORMAL,
        type: "symbol",
        source: SOURCE_ID,
        layout: {
          "icon-image": ["get", "pinImg"],
          "icon-size": 1,
          "icon-anchor": "center",
          "icon-allow-overlap": true
        }
      });
      map.addLayer({
        id: LAYER_DIMMED,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["==", "id", "__none__"],
        layout: {
          "icon-image": ["get", "pinImgDim"],
          "icon-size": 0.75,
          "icon-anchor": "center",
          "icon-allow-overlap": true
        }
      });
      map.addLayer({
        id: LAYER_SELECTED,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["==", "id", "__none__"],
        layout: {
          "icon-image": ["get", "pinImgSel"],
          "icon-size": 1.4,
          "icon-anchor": "center",
          "icon-allow-overlap": true
        }
      });
      layersReadyRef.current = true;
      applyData(map, latestPlaces.current);
      applySelection(map, latestSelected.current);
    };
    setup();
    return () => {
      cancelled = true;
      layersReadyRef.current = false;
      try {
        for (const layer of [LAYER_NORMAL, LAYER_DIMMED, LAYER_SELECTED]) {
          if (map.getLayer(layer)) map.removeLayer(layer);
        }
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // ignore — map may already be torn down
      }
    };
  }, [isLoaded, map]);

  // Click pin → select; click empty → clear
  useEffect(() => {
    if (!isLoaded || !map) return;
    const layerIds = [LAYER_NORMAL, LAYER_DIMMED, LAYER_SELECTED];
    const onPinClick = e => {
      const f = e.features?.[0];
      if (f?.properties?.id) {
        e.originalEvent.stopPropagation();
        cbSelect.current?.(f.properties.id);
      }
    };
    const onEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = "";
    };
    const onMapClick = e_0 => {
      const hits = map.queryRenderedFeatures(e_0.point, {
        layers: layerIds
      });
      if (hits.length === 0) cbClear.current?.();
    };
    for (const layer_0 of layerIds) {
      map.on("click", layer_0, onPinClick);
      map.on("mouseenter", layer_0, onEnter);
      map.on("mouseleave", layer_0, onLeave);
    }
    map.on("click", onMapClick);
    return () => {
      for (const layer_1 of layerIds) {
        map.off("click", layer_1, onPinClick);
        map.off("mouseenter", layer_1, onEnter);
        map.off("mouseleave", layer_1, onLeave);
      }
      map.off("click", onMapClick);
    };
  }, [isLoaded, map]);

  // Hover popup with place name — desktop only (skip on touch devices
  // where tap triggers both mouseenter and click, and the drawer already
  // shows the place info on click).
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!window.matchMedia("(hover: hover)").matches) return;
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: [0, -18]
    });
    const layerIds_0 = [LAYER_NORMAL, LAYER_DIMMED];
    const onEnter_0 = e_1 => {
      const f_0 = e_1.features?.[0];
      if (f_0?.properties?.name && f_0.geometry.type === "Point") {
        const pill = document.createElement("div");
        pill.className = "ffw-tooltip rounded-md px-2 py-1 text-xs font-semibold shadow-md";
        pill.textContent = String(f_0.properties.name);
        popup.setLngLat(f_0.geometry.coordinates).setDOMContent(pill).addTo(map);
      }
    };
    const onLeave_0 = () => popup.remove();
    for (const layer_2 of layerIds_0) {
      map.on("mouseenter", layer_2, onEnter_0);
      map.on("mouseleave", layer_2, onLeave_0);
    }
    return () => {
      for (const layer_3 of layerIds_0) {
        map.off("mouseenter", layer_3, onEnter_0);
        map.off("mouseleave", layer_3, onLeave_0);
      }
      popup.remove();
    };
  }, [isLoaded, map]);

  // Sync places (skip empty wipes during flyTo cache miss)
  useEffect(() => {
    if (!map || !layersReadyRef.current) return;
    if (places.length === 0 && prevPlacesCount.current > 0) return;
    prevPlacesCount.current = places.length;
    applyData(map, places);
  }, [map, places]);

  // Sync selection (filters only, no layout writes)
  useEffect(() => {
    if (!map || !layersReadyRef.current) return;
    applySelection(map, selectedPlaceId);
  }, [map, selectedPlaceId]);
  return null;
}